import qs from 'qs'

// 每个操作独立保存；摘要仅用于查找，不再作为可被其他标签页覆盖的存储键。
const prefix = 'kd.member-removal.v2.'
const legacyPrefix = 'kd.member-removal.v1.'
const legacyDonePrefix = 'kd.member-removal.legacy-done.v2.'
const retryUrl = '/openapi/Membersync/retry'
const config = { memberSync: true }
let recovering = false
const inflight = new Map()
let serverClock = null

function validCredentials(value) {
  return value && /^[a-f0-9]{32}$/.test(value.id) && /^[a-f0-9]{64}$/.test(value.token)
}

function removeMatchingRecord(record) {
  // 始终只处理操作编号对应的独立键，绝不清理仍可能被旧页面使用的摘要键。
  const key = prefix + record.id
  try {
    const current = JSON.parse(localStorage.getItem(key) || 'null')
    if (current && current.id === record.id && current.token === record.token) localStorage.removeItem(key)
  } catch (error) {
    // 清理失败保留记录；后续扫描到终态标记时继续重试，不阻塞其他操作。
  }
}

function legacyCompleted(record) {
  if (localStorage.getItem(legacyDonePrefix + record.id) !== record.token) return false
  removeMatchingRecord(record)
  return true
}

function records() {
  const result = new Map()
  const keys = []
  for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i))
  for (const key of keys) {
    if (!key || (!key.startsWith(prefix) && !key.startsWith(legacyPrefix))) continue
    let value
    try { value = JSON.parse(localStorage.getItem(key)) } catch (error) { continue }
    if (!validCredentials(value)) continue
    const legacy = key.startsWith(legacyPrefix)
    const requestKey = legacy ? key.slice(legacyPrefix.length) : value.requestKey
    if (!/^[a-f0-9]{64}$/.test(requestKey || '')) continue
    const operationKey = prefix + value.id
    if (!legacy && key !== operationKey) continue
    if (legacyCompleted(value)) continue
    let record = { id: value.id, token: value.token, requestKey, legacy: legacy || value.legacy === true, key: operationKey }
    if (legacy) {
      // 先复制到独立键，绝不修改旧摘要键：旧页面可能仍在该键写入另一个操作。
      const saved = localStorage.getItem(operationKey)
      if (saved) {
        let existing
        try { existing = JSON.parse(saved) } catch (error) { continue }
        if (!validCredentials(existing) || existing.id !== record.id || existing.token !== record.token
          || existing.requestKey !== requestKey) continue
        record = { ...existing, key: operationKey }
      } else {
        try {
          localStorage.setItem(operationKey, JSON.stringify({ id: record.id, token: record.token, requestKey, legacy: true }))
        } catch (error) {
          // 复制失败仍用已读取的原凭据恢复；不改写旧键，也不阻塞其他操作。
        }
      }
    }
    // 旧暂停标记不再阻止查询；凭据内容不随 pending/not_found 回执反复改写。
    if (!legacyCompleted(record)) result.set(record.id, record)
  }
  return Array.from(result.values()).sort((a, b) => a.id.localeCompare(b.id))
}

function hex(bytes) {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

function credentials(record) {
  return { member_sync_id: record.id, member_sync_token: record.token }
}

function accept(record, response) {
  const receipt = response.data && response.data.data && response.data.data.member_sync
  if (!receipt || receipt.operation_id !== record.id) return response
  if (Number.isSafeInteger(receipt.server_time) && receipt.server_time > 0 && receipt.server_time <= 0xffffffff) {
    serverClock = { milliseconds: receipt.server_time * 1000, tick: performance.now() }
  }
  if (['completed', 'cancelled'].includes(receipt.state)) {
    // 仅旧记录需要终态标记，避免尚未关闭的旧页面或并发迁移再次导入它。
    // 普通新任务完成后只删除自己的独立键，不持续累积本地终态记录。
    try {
      if (record.legacy) localStorage.setItem(legacyDonePrefix + record.id, record.token)
      removeMatchingRecord(record)
    } catch (error) {
      // 本地清理失败不否定服务端回执；保留凭据，下次恢复再确认并清理。
      // 旧终态标记写入失败时不会删除独立键，避免旧记录重新导入后丢失凭据。
    }
  }
  // pending/not_found/旧版 invalid_clock 均保留凭据；不写暂停状态，避免迟到回执复活已完成记录。
  return response
}

function createRecord(requestKey) {
  const now = serverClock ? serverClock.milliseconds + performance.now() - serverClock.tick : Date.now()
  const issuedAt = Math.floor(now / 1000)
  if (!Number.isSafeInteger(issuedAt) || issuedAt <= 0 || issuedAt > 0xffffffff) throw new Error('操作时间无效')
  const record = { id: issuedAt.toString(16).padStart(8, '0') + hex(window.crypto.getRandomValues(new Uint8Array(12))), token: hex(window.crypto.getRandomValues(new Uint8Array(32))), requestKey }
  record.key = prefix + record.id
  if (localStorage.getItem(record.key) !== null) throw new Error('操作编号冲突，请重试')
  localStorage.setItem(record.key, JSON.stringify({ id: record.id, token: record.token, requestKey }))
  return record
}

function assertIndependentRecord(record) {
  // 恢复已登记任务可以使用旧凭据；提交尚未登记业务必须先保存到独立键。
  if (legacyCompleted(record)) throw new Error('原操作已结束，请重新操作')
  const key = prefix + record.id
  if (localStorage.getItem(key) === null) {
    const saved = { id: record.id, token: record.token, requestKey: record.requestKey }
    if (record.legacy) saved.legacy = true
    localStorage.setItem(key, JSON.stringify(saved))
  }
  // 写入失败、并发删除或凭据不匹配都不能继续提交；已有不同记录绝不覆盖。
  const current = JSON.parse(localStorage.getItem(key) || 'null')
  if (!current || current.id !== record.id || current.token !== record.token
    || current.requestKey !== record.requestKey || (record.legacy && current.legacy !== true)) {
    throw new Error('恢复凭据未成功保存，请稍后重试')
  }
  // 防止另一个标签页完成操作后，迟到的持久化再次留下恢复记录。
  if (legacyCompleted(record)) throw new Error('原操作已结束，请重新操作')
}

function retry(client, record) {
  if (inflight.has(record.id)) return inflight.get(record.id)
  const task = client.post(retryUrl, qs.stringify(credentials(record)), config)
    .then(response => accept(record, response))
    .then(response => { inflight.delete(record.id); return response }, error => { inflight.delete(record.id); throw error })
  inflight.set(record.id, task)
  return task
}

export async function submitMemberChange(client, url, body, isCurrent = null) {
  let record
  try {
    // Cookie 只参与内存摘要；不保存表单、密码或登录 Cookie。
    const input = new TextEncoder().encode(url + '\n' + body + '\n' + document.cookie)
    const requestKey = hex(new Uint8Array(await window.crypto.subtle.digest('SHA-256', input)))
    let completedResponse
    for (const candidate of records().filter(item => item.requestKey === requestKey)) {
      const response = await retry(client, candidate)
      const receipt = response.data && response.data.data && response.data.data.member_sync
      if (!receipt || receipt.operation_id !== candidate.id) return response
      if (receipt.state === 'completed') completedResponse = response
      else if (receipt.state === 'not_found') record = record || candidate
      else if (receipt.state !== 'cancelled') return response
    }
    // 同一表单只要已有完成回执或未确认任务，本次点击就不另建删除操作。
    if (completedResponse) return completedResponse
    // 已登记任务查询不受新页面权限限制；只有首次业务提交必须仍属于原上下文。
    if (isCurrent && isCurrent() !== true) return { data: { code: 0, msg: '页面或登录信息已变化，请刷新后操作' } }
    record = record || createRecord(requestKey)
    assertIndependentRecord(record)
  } catch (error) {
    return { data: { code: 0, msg: '暂时无法准备或恢复同步操作，请检查网络及浏览器存储后重试' } }
  }
  try {
    if (isCurrent && isCurrent() !== true) return { data: { code: 0, msg: '页面或登录信息已变化，已保留原恢复凭据' } }
    const submitConfig = isCurrent ? { ...config, isErrorContextCurrent: () => isCurrent() === true,
      transformRequest: [data => {
        // 在 Axios 真正发送前再核对，避免请求拦截等待期间页面失效。
        if (isCurrent() !== true) {
          const error = new Error('原确认上下文已失效')
          error.config = submitConfig // 交给既有全局提示过滤及请求收尾，不泄露原恢复凭据。
          throw error
        }
        return data
      }] } : config
    const response = await client.post(url, qs.stringify({ ...qs.parse(body), ...credentials(record) }), submitConfig)
    return accept(record, response)
  } catch (error) {
    return { data: { code: 0, msg: '操作结果暂未确认，已保留恢复记录，请稍后重试' } }
  }
}

export function startMemberRecovery(client, notify) {
  let delay = 60000
  let timer
  async function recover() {
    if (recovering) return
    clearTimeout(timer)
    recovering = true
    let completed = 0
    try {
      for (const record of records()) {
        try {
          const response = await retry(client, record)
          const receipt = response.data && response.data.data && response.data.data.member_sync
          if (receipt && receipt.operation_id === record.id && receipt.state === 'completed') completed++
          // 自动恢复只查询、补发和清理；绝不创建编号或重放业务表单。
        } catch (error) { /* 保留凭据，网络及登录恢复后继续查询。 */ }
      }
      if (completed) notify({ message: '此前待确认的成员同步已完成', type: 'success' })
    } catch (error) { /* 存储不可用时不执行新的业务修改。 */ }
    recovering = false
    timer = setTimeout(recover, delay)
    delay = Math.min(delay * 2, 300000)
  }
  window.addEventListener('online', recover)
  window.addEventListener('focus', recover)
  recover()
}



// WEBPACK FOOTER //
// ./src/assest/utils/memberRemovalSync.js